/**
 * useSortableList - Gesture + Reanimated hook for drag-to-reorder.
 *
 * Long-press activates drag, pan tracks movement, neighbours animate with
 * springs. Supports VARIABLE row heights (single/multi-line titles, cards with
 * subtasks) via a measured `heights` array, plus edge auto-scroll for long
 * lists. Drag start/end is reported synchronously so the parent can lock the
 * scroll view without a polling race.
 *
 * Compatible with react-native-reanimated v4 (uses Gesture API, not
 * useAnimatedGestureHandler).
 */

import { useCallback, useEffect, useRef } from 'react';
import { Gesture, type SimultaneousGesture } from 'react-native-gesture-handler';
import {
  runOnJS,
  useSharedValue,
  withTiming,
  type AnimatedRef,
  type SharedValue,
} from 'react-native-reanimated';
import type Animated from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';

import { computeHoverIndex } from '@/lib/todo/sortable-geometry';
import { useSortableAutoScroll } from '@/hooks/use-sortable-autoscroll';

const LONG_PRESS_MS = 200;

export type SortableListState = {
  activeIndex: SharedValue<number>;
  hoverIndex: SharedValue<number>;
  dragY: SharedValue<number>;
  heights: SharedValue<number[]>;
  createDragGesture: (index: number) => SimultaneousGesture;
  measureItem: (taskId: string, height: number) => void;
};

type SortableListConfig = {
  itemCount: number;
  /** Fallback row height used until a row reports its real height. */
  fallbackHeight: number;
  onReorder: (taskId: string, toIndex: number) => void;
  getItemId: (index: number) => string;
  /** Called on the JS thread the moment a drag starts (true) / ends (false). */
  onDragStateChange: (isDragging: boolean) => void;
  scrollRef: AnimatedRef<Animated.ScrollView>;
  /** Window Y of the scroll viewport's top edge. */
  viewportTop: number;
  /** Window Y of the scroll viewport's bottom edge. */
  viewportBottom: number;
};

export function useSortableList({
  itemCount,
  fallbackHeight,
  onReorder,
  getItemId,
  onDragStateChange,
  scrollRef,
  viewportTop,
  viewportBottom,
}: SortableListConfig): SortableListState {
  const activeIndex = useSharedValue(-1);
  const hoverIndex = useSharedValue(-1);
  const dragY = useSharedValue(0);
  const lastTranslationY = useSharedValue(0);
  const autoScrollDelta = useSharedValue(0);
  const absoluteY = useSharedValue(0);
  const heights = useSharedValue<number[]>([]);

  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;
  const getItemIdRef = useRef(getItemId);
  getItemIdRef.current = getItemId;
  const onDragStateChangeRef = useRef(onDragStateChange);
  onDragStateChangeRef.current = onDragStateChange;

  // Real row heights keyed by task id so values survive reorders/inserts.
  const heightByIdRef = useRef<Map<string, number>>(new Map());

  const rebuildHeights = useCallback(() => {
    const next: number[] = [];
    for (let i = 0; i < itemCount; i += 1) {
      const id = getItemIdRef.current(i);
      next.push(heightByIdRef.current.get(id) ?? fallbackHeight);
    }
    heights.value = next;
  }, [itemCount, fallbackHeight, heights]);

  // Rebuild whenever the set/order of items changes (getItemId is memoised on tasks).
  useEffect(() => {
    rebuildHeights();
  }, [rebuildHeights, getItemId]);

  const measureItem = useCallback(
    (taskId: string, height: number) => {
      const prev = heightByIdRef.current.get(taskId);
      if (prev !== undefined && Math.abs(prev - height) < 0.5) {
        return;
      }
      heightByIdRef.current.set(taskId, height);
      rebuildHeights();
    },
    [rebuildHeights],
  );

  const triggerHaptic = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
  }, []);

  // Re-derive dragY + hover index from the latest finger + auto-scroll state.
  // Called both on pan move and on every auto-scroll frame (finger may be still).
  const updateDrag = useCallback(() => {
    'worklet';
    dragY.value = lastTranslationY.value + autoScrollDelta.value;
    const count = heights.value.length;
    const next = computeHoverIndex(activeIndex.value, dragY.value, heights.value, count);
    if (next !== hoverIndex.value) {
      hoverIndex.value = next;
      runOnJS(triggerHaptic)();
    }
  }, [dragY, lastTranslationY, autoScrollDelta, heights, activeIndex, hoverIndex, triggerHaptic]);

  const autoScroll = useSortableAutoScroll({
    scrollRef,
    activeIndex,
    absoluteY,
    autoScrollDelta,
    viewportTop,
    viewportBottom,
    onScroll: updateDrag,
  });

  const handleDragStart = useCallback(() => {
    triggerHaptic();
    autoScroll.setActive(true);
    onDragStateChangeRef.current(true);
  }, [triggerHaptic, autoScroll]);

  const handleDragEnd = useCallback(() => {
    autoScroll.setActive(false);
    onDragStateChangeRef.current(false);
  }, [autoScroll]);

  const handleDrop = useCallback((fromIdx: number, toIdx: number) => {
    if (fromIdx !== toIdx && fromIdx >= 0 && toIdx >= 0) {
      const taskId = getItemIdRef.current(fromIdx);
      onReorderRef.current(taskId, toIdx);
    }
  }, []);

  const createDragGesture = useCallback(
    (index: number): SimultaneousGesture => {
      const longPress = Gesture.LongPress()
        .minDuration(LONG_PRESS_MS)
        .onStart(() => {
          activeIndex.value = index;
          hoverIndex.value = index;
          dragY.value = 0;
          lastTranslationY.value = 0;
          autoScrollDelta.value = 0;
          runOnJS(handleDragStart)();
        });

      const pan = Gesture.Pan()
        .manualActivation(true)
        .onTouchesMove((_event, stateManager) => {
          // Only activate once the long press has lifted the card. Never fail
          // here: finger jitter fires this before the long press completes,
          // and a failed pan can't re-activate for the same touch — which
          // left the card lifted but undraggable.
          if (activeIndex.value >= 0) {
            stateManager.activate();
          }
        })
        .onUpdate((event) => {
          lastTranslationY.value = event.translationY;
          absoluteY.value = event.absoluteY;
          updateDrag();
        })
        .onEnd(() => {
          const fromIdx = activeIndex.value;
          const toIdx = hoverIndex.value;
          dragY.value = withTiming(0, { duration: 120 });
          runOnJS(handleDrop)(fromIdx, toIdx);
        })
        .onFinalize(() => {
          dragY.value = withTiming(0, { duration: 100 });
          autoScrollDelta.value = 0;
          lastTranslationY.value = 0;
          activeIndex.value = -1;
          hoverIndex.value = -1;
          runOnJS(handleDragEnd)();
        });

      return Gesture.Simultaneous(longPress, pan);
    },
    [
      activeIndex,
      hoverIndex,
      dragY,
      lastTranslationY,
      autoScrollDelta,
      absoluteY,
      updateDrag,
      handleDragStart,
      handleDragEnd,
      handleDrop,
    ],
  );

  return {
    activeIndex,
    hoverIndex,
    dragY,
    heights,
    createDragGesture,
    measureItem,
  };
}
